import {FC, useRef} from 'react'
import {UseFormReturn} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {useTranslation} from '@/translations/i18n'
import {MailerFormValidationSchema} from '@/features/mailer/types'
import {Label} from '@/components/ui/label/Label'
import {Button} from '@/components/ui/button/Button'
import {XCloseIcon, Attachment02Icon} from '@/components/ui/icon/Icon'
import {ProgressBar} from '@/components/ui/progress-bar/ProgressBar'
import {toast} from '@/utilities/toast/toast'
import {formatBytes} from '@/features/mailer/utils'
import {ALLOWED_ATTACHMENT_TYPES, MAX_TOTAL_ATTACHMENT_SIZE} from '@/features/mailer/constants'
import {
    StyledMailerSection,
    StyledAttachmentsList,
    StyledAttachmentChip,
    StyledAttachmentMeta,
    StyledAttachmentSummary
} from '@/pages/mailer/style'

interface MailerComposeSectionProps {
    form: UseFormReturn<MailerFormValidationSchema>
    attachments: File[]
    onAttachmentsChange: (files: File[]) => void
}

export const MailerComposeSection: FC<MailerComposeSectionProps> = ({form, attachments, onAttachmentsChange}) => {
    const {t} = useTranslation()
    const {
        register,
        formState: {errors}
    } = form
    const fileInputRef = useRef<HTMLInputElement>(null)

    const totalSize = attachments.reduce((sum, f) => sum + f.size, 0)
    const sizePercentage = Math.min(100, Math.round((totalSize / MAX_TOTAL_ATTACHMENT_SIZE) * 100))
    const sizeVariant = sizePercentage >= 85 ? 'danger' : 'primary'

    const resetFileInput = () => {
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = Array.from(event.target.files ?? [])
        if (newFiles.length === 0) {
            resetFileInput()
            return
        }

        const invalidType = newFiles.find(f => !ALLOWED_ATTACHMENT_TYPES.includes(f.type))
        if (invalidType) {
            toast.error(t('mailer:attachments_invalid_type', {name: invalidType.name}))
            resetFileInput()
            return
        }

        const projectedTotal = [...attachments, ...newFiles].reduce((sum, f) => sum + f.size, 0)
        if (projectedTotal > MAX_TOTAL_ATTACHMENT_SIZE) {
            toast.error(t('mailer:attachments_too_large', {limit: formatBytes(MAX_TOTAL_ATTACHMENT_SIZE)}))
            resetFileInput()
            return
        }

        onAttachmentsChange([...attachments, ...newFiles])
        resetFileInput()
    }

    const handleRemoveAttachment = (index: number) => {
        onAttachmentsChange(attachments.filter((_, i) => i !== index))
    }

    const handleOpenFile = (file: File) => {
        window.open(URL.createObjectURL(file), '_blank')
    }

    return (
        <StyledMailerSection>
            <Label>{t('mailer:email_section_title')}</Label>

            <InputText
                label={t('mailer:subject')}
                type="text"
                placeholder={t('mailer:subject_placeholder')}
                errorMessage={errors.subject?.message}
                {...register('subject')}
            />

            <TextArea
                label={t('mailer:body')}
                placeholder={t('mailer:body_placeholder')}
                errorMessage={errors.body?.message}
                rows={10}
                {...register('body')}
            />

            <Flexbox direction="column" gap={2} fullWidth>
                <Flexbox align="center" gap={2}>
                    <Label>{t('mailer:attachments')}</Label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        style={{display: 'none'}}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <Attachment02Icon size={16} />
                        {t('mailer:add_files')}
                    </Button>
                </Flexbox>

                {attachments.length > 0 && (
                    <>
                        <StyledAttachmentsList>
                            {attachments.map((file, index) => (
                                <StyledAttachmentChip key={`${file.name}-${index}`}>
                                    <button
                                        type="button"
                                        onClick={() => handleOpenFile(file)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            textDecoration: 'underline'
                                        }}
                                    >
                                        {file.name}
                                    </button>
                                    <StyledAttachmentMeta>{formatBytes(file.size)}</StyledAttachmentMeta>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        shape="text"
                                        onClick={() => handleRemoveAttachment(index)}
                                    >
                                        <XCloseIcon size={14} />
                                    </Button>
                                </StyledAttachmentChip>
                            ))}
                        </StyledAttachmentsList>

                        <StyledAttachmentSummary>
                            <span>
                                {t('mailer:attachments_total', {
                                    count: attachments.length,
                                    size: formatBytes(totalSize),
                                    limit: formatBytes(MAX_TOTAL_ATTACHMENT_SIZE)
                                })}
                            </span>
                            <ProgressBar percentage={sizePercentage} variant={sizeVariant} rounded />
                        </StyledAttachmentSummary>
                    </>
                )}
            </Flexbox>
        </StyledMailerSection>
    )
}
